"use client"

import type { ICustomAuthPublicClientApplication } from "@azure/msal-browser/custom-auth"
import { notifyAzureAuthChanged } from "@/lib/azure/auth-events"

type UserType = "company" | "job_seeker"
type AuthError = { message: string; code?: string }

type NativeContext = {
  userType: UserType
  intent: "login" | "signup"
  returnTo: string
  termsAccepted?: boolean
  companyProfile?: {
    companyName?: string
    phoneNumber?: string
    sector?: string
    companySize?: string
    website?: string
    location?: string
    description?: string
    preferredLanguage?: string
  }
}

let clientPromise: Promise<ICustomAuthPublicClientApplication> | undefined
let pendingSignUp: any
let pendingSignIn: any
let pendingReset: any
let pendingContext: NativeContext | undefined

function contextFromLocation(intent: "login" | "signup" = "login"): NativeContext {
  const pathname = window.location.pathname
  const userType: UserType = pathname.includes("/employer")
    ? "company"
    : "job_seeker"
  const search = new URLSearchParams(window.location.search)
  const requested = search.get("redirect") || search.get("returnTo")
  const safeRequested =
    requested?.startsWith("/") && !requested.startsWith("//")
      ? requested
      : null
  const applicationReturnTo = pathname.startsWith("/applicant/apply")
    ? `${pathname}${window.location.search}`
    : null

  return {
    userType,
    intent,
    returnTo:
      safeRequested || applicationReturnTo ||
      (userType === "company" ? "/employer/jobs" : "/applicant/dashboard"),
  }
}

async function nativeClient(): Promise<ICustomAuthPublicClientApplication> {
  if (!clientPromise) {
    clientPromise = (async () => {
      const response = await fetch("/api/auth/native/config", {
        credentials: "same-origin",
        cache: "no-store",
      })
      const config = await response.json()
      if (!response.ok) {
        throw new Error(config.error || "Native authentication is unavailable.")
      }

      const { CustomAuthPublicClientApplication } = await import(
        "@azure/msal-browser/custom-auth"
      )
      return CustomAuthPublicClientApplication.create({
        auth: {
          clientId: config.clientId,
          authority: `https://${config.tenantSubdomain}.ciamlogin.com`,
          knownAuthorities: [`${config.tenantSubdomain}.ciamlogin.com`],
        },
        cache: {
          cacheLocation: "sessionStorage",
        },
        customAuth: {
          authApiProxyUrl: `${window.location.origin}/api/auth/native/proxy`,
          challengeTypes: ["password", "oob", "redirect"],
        },
      })
    })()
  }
  return clientPromise
}

function messageFromResult(result: any, fallback: string): string {
  const error = result?.error?.errorData || result?.error || result
  const code = String(error?.errorCode || error?.error || "").toLowerCase()
  const suberror = String(
    error?.suberror || error?.subError || error?.errorSubCode || "",
  ).toLowerCase()
  const description = String(
    error?.errorDescription || error?.message || fallback,
  )
  const searchable = `${code} ${suberror} ${description}`.toLowerCase()

  if (searchable.includes("password_banned")) {
    return "Choose a different password. This password contains a word, phrase, or pattern that Microsoft blocks."
  }
  if (searchable.includes("password_too_short")) {
    return "Password must be at least 8 characters long."
  }
  if (searchable.includes("password_too_long")) {
    return "Password must be no more than 256 characters long."
  }
  if (searchable.includes("password_recently_used")) {
    return "Choose a password you have not used recently."
  }
  if (searchable.includes("password_too_weak") || searchable.includes("password_is_invalid")) {
    return "Choose a stronger password that meets the password requirements."
  }

  if (code.includes("password_incorrect") || code.includes("invalid_grant")) {
    return "Invalid login credentials"
  }
  if (code.includes("user_not_found") || code.includes("invalid_username")) {
    return "Invalid login credentials"
  }
  if (code.includes("user_already_exists")) {
    return "User already registered"
  }
  if (code.includes("invalid_oob") || code.includes("invalid_code")) {
    return "Token has expired or is invalid"
  }
  if (code.includes("throttled") || description.toLowerCase().includes("too many")) {
    return "Too many requests. Please try again later."
  }
  return description || fallback
}

function failure(result: any, fallback: string): { data: null; error: AuthError } {
  return { data: null, error: { message: messageFromResult(result, fallback) } }
}

async function establishApplicationSession(account: any, context: NativeContext) {
  const idToken = account?.getIdToken?.()
  if (!idToken) {
    return failure(null, "External ID did not return an identity token.")
  }

  const response = await fetch("/api/auth/native/session", {
    method: "POST",
    credentials: "same-origin",
    cache: "no-store",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ idToken, ...context }),
  })
  const result = await response.json().catch(() => ({}))
  if (!response.ok) {
    return failure(result, result.error || "Sign-in could not be completed.")
  }

  await notifyAzureAuthChanged()

  return {
    data: {
      user: result.user,
      session: result.session,
      destination: result.destination,
    },
    error: null,
  }
}

export async function nativeSignIn(input: {
  email: string
  password: string
}) {
  try {
    pendingContext = contextFromLocation("login")
    const client = await nativeClient()
    const current = client.getCurrentAccount()
    if (current.isCompleted() && current.data) {
      return establishApplicationSession(current.data, pendingContext)
    }
    const result = await client.signIn({
      username: input.email.trim().toLowerCase(),
      password: input.password,
      scopes: ["openid", "profile", "email", "offline_access"],
    })

    if (result.isCompleted() && result.data) {
      return establishApplicationSession(result.data, pendingContext)
    }
    if (result.isCodeRequired()) {
      pendingSignIn = result.state
      return failure({ message: "Email not confirmed" }, "Email not confirmed")
    }
    return failure(result, "Invalid login credentials")
  } catch (error) {
    return failure(error, "Sign-in failed.")
  }
}

export async function nativeSignUp(input: {
  email: string
  password: string
  options?: { data?: Record<string, any> }
}) {
  try {
    const metadata = input.options?.data || {}
    pendingContext = contextFromLocation("signup")
    pendingContext.userType =
      metadata.user_type === "company" ? "company" : "job_seeker"
    if (pendingContext.userType === "company") {
      pendingContext.termsAccepted = metadata.terms_accepted === true
      pendingContext.companyProfile = metadata.company_profile
    }
    const displayName = String(
      metadata.company_name || metadata.full_name || input.email.split("@")[0],
    )
    const client = await nativeClient()
    const current = client.getCurrentAccount()
    if (current.isCompleted() && current.data) {
      await current.data.signOut()
    }
    pendingSignIn = undefined
    pendingSignUp = undefined
    const result = await client.signUp({
      username: input.email.trim().toLowerCase(),
      password: input.password,
      attributes: { displayName },
    })

    if (result.isCodeRequired()) {
      pendingSignUp = result.state
      return {
        data: {
          user: {
            id: "pending-external-id-user",
            email: input.email.trim().toLowerCase(),
            user_metadata: metadata,
          },
          session: null,
        },
        error: null,
      }
    }
    return failure(result, "Account creation failed.")
  } catch (error) {
    return failure(error, "Account creation failed.")
  }
}

export async function nativeVerifyOtp(input: {
  token: string
  type: string
}) {
  try {
    if (input.type === "recovery") {
      if (!pendingReset?.submitCode) {
        return failure(null, "Start password recovery again.")
      }
      const result = await pendingReset.submitCode(input.token)
      if (!result.isPasswordRequired()) {
        return failure(result, "Token has expired or is invalid")
      }
      pendingReset = result.state
      return { data: { user: null, session: null }, error: null }
    }

    if (pendingSignUp?.submitCode) {
      const result = await pendingSignUp.submitCode(input.token)
      if (!result.isCompleted()) {
        return failure(result, "Token has expired or is invalid")
      }
      const signedIn = await result.state.signIn({
        scopes: ["openid", "profile", "email", "offline_access"],
      })
      if (!signedIn.isCompleted() || !signedIn.data || !pendingContext) {
        return failure(signedIn, "Verification succeeded, but sign-in failed.")
      }
      pendingSignUp = undefined
      return establishApplicationSession(signedIn.data, pendingContext)
    }

    if (pendingSignIn?.submitCode) {
      const result = await pendingSignIn.submitCode(input.token)
      if (!result.isCompleted() || !result.data || !pendingContext) {
        return failure(result, "Token has expired or is invalid")
      }
      pendingSignIn = undefined
      return establishApplicationSession(result.data, pendingContext)
    }

    return failure(null, "Start verification again.")
  } catch (error) {
    return failure(error, "Verification failed.")
  }
}

export async function nativeResend(input: { type: string }) {
  try {
    const state = input.type === "recovery"
      ? pendingReset
      : pendingSignUp || pendingSignIn
    if (!state?.resendCode) {
      return failure(null, "Start verification again.")
    }
    const result = await state.resendCode()
    if (result.isFailed?.()) {
      return failure(result, "Verification code could not be sent.")
    }
    if (input.type === "recovery") pendingReset = result.state
    else if (pendingSignUp) pendingSignUp = result.state
    else pendingSignIn = result.state
    return { data: {}, error: null }
  } catch (error) {
    return failure(error, "Verification code could not be sent.")
  }
}

export async function nativeResetPassword(email: string) {
  try {
    const client = await nativeClient()
    const result = await client.resetPassword({
      username: email.trim().toLowerCase(),
    })
    if (!result.isCodeRequired()) {
      return failure(result, "Password recovery could not be started.")
    }
    pendingReset = result.state
    return { data: {}, error: null }
  } catch (error) {
    return failure(error, "Password recovery could not be started.")
  }
}

export async function nativeUpdateUser(input: { password?: string }) {
  try {
    if (!input.password || !pendingReset?.submitNewPassword) {
      return failure(null, "Verify the password recovery code first.")
    }
    const result = await pendingReset.submitNewPassword(input.password)
    if (!result.isCompleted()) {
      return failure(result, "Password could not be updated.")
    }
    pendingReset = undefined
    return { data: { user: null }, error: null }
  } catch (error) {
    return failure(error, "Password could not be updated.")
  }
}

export async function clearNativeAccount() {
  try {
    const client = await nativeClient()
    const current = client.getCurrentAccount()
    if (current.isCompleted() && current.data) {
      await current.data.signOut()
    }
  } catch {
    // The application cookie is authoritative; clearing it still signs out.
  }
}
